/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { Box, makeStyles, Tooltip } from "@material-ui/core";
import React from "react";
import { useSelector } from "react-redux";
import { VscNewFolder } from "react-icons/vsc";
import { HiOutlineLockClosed } from "react-icons/hi";
import { roiNumerator } from "lib/roi/strategies";

const useStyles = makeStyles((theme) => ({
  matrixCell: {
    border: "2px solid #E0E0E0",
    boxSizing: "border-box",
    width: "100%",
    minWidth: "130px",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: 4,
    "&:nth-child(odd)": {
      backgroundColor: "#f5f5f5",
    },
    "&:nth-child(even)": {
      backgroundColor: "#ffffff",
    },
    [theme.breakpoints.up(2560)]: { padding: "10px 30px" },
    [theme.breakpoints.between(1920, 2560)]: { padding: "10px 20px" },
    [theme.breakpoints.between(1440, 1920)]: { padding: "10px 10px" },
    [theme.breakpoints.between(1280, 1440)]: { padding: "10px 10px" },
    [theme.breakpoints.down(1280)]: { padding: "10px 10px" },
  },
  emptyCell: {
    border: "none",
    backgroundColor: "#ffffff !important",
  },
  noDataCell: {
    border: "dotted 2px #E0E0E0",
  },
  cellKpiContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tooltipCellInfo: {
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "10px",
    padding: "10px 10px",
    [theme.breakpoints.up(2560)]: { fontSize: "16px" },
    [theme.breakpoints.between(1920, 2560)]: { fontSize: "14.5px" },
    [theme.breakpoints.between(1440, 1920)]: { fontSize: "13px" },
    [theme.breakpoints.between(1280, 1440)]: { fontSize: "12px" },
    [theme.breakpoints.down(1280)]: { fontSize: "12px" },
  },
}));

const KPI_DEFINITIONS = [
  { name: "Price", key: "price", isCurrency: true },
  { name: "Lift Price", key: "liftPrice" },
  { name: "Lift Units", key: "liftUnits" },
  { name: "Lift Sales", key: "liftSales" },
  { name: "Lift Margin", key: "liftMargin" },
  { name: "Lift Ret. Margin", key: "liftRetailerMargin" },
  { name: "ROI", key: "roi" },
];

function roundToTwoDecimals(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

const calculateWeightedPrice = (scenarioData) => {
  if (!scenarioData || scenarioData.length === 0) return 0;
  const { dividend, divisor } = scenarioData.reduce(
    (acc, { avg_weight_by_units_price, sum_units }) => ({
      dividend: acc.dividend + avg_weight_by_units_price * sum_units,
      divisor: acc.divisor + sum_units,
    }),
    { dividend: 0, divisor: 0 }
  );
  return dividend / divisor;
};

const calculateAggregates = (scenarioData, key) => {
  if (!scenarioData || scenarioData.length === 0) return 0;
  return scenarioData.reduce((total, item) => total + item[key], 0);
};

const calculateROI = (baseData, compareData, roiStrategy = "standard") => {
  const valuesForROI = {
    baseMargin: 0,
    compareMargin: 0,
    investmentIn: 0,
    baseUnits: 0,
    compareUnits: 0,
  };
  if (
    !baseData ||
    baseData.length === 0 ||
    !compareData ||
    compareData.length === 0
  )
    return 0;
  const {
    baseMargin,
    compareMargin,
    investmentIn,
    baseUnits,
    compareUnits,
  } = compareData.reduce((acc, row) => {
    const baseRow = baseData.find(
      (item) =>
        item.product_id === row.product_id &&
        item.retailer_id === row.retailer_id &&
        item.area_id === row.area_id
    );
    // Row with a price drop (promo): same condition previously used for the margin.
    const priceDropped =
      baseRow?.avg_weight_by_units_price > row.avg_weight_by_units_price;
    return {
      baseMargin: acc.baseMargin + (priceDropped ? baseRow?.sum_margin : 0),
      compareMargin: acc.compareMargin + (priceDropped ? row.sum_margin : 0),
      investmentIn:
        acc.investmentIn +
        (baseRow?.avg_weight_by_units_in_price_in >
        row.avg_weight_by_units_in_price_in
          ? (baseRow?.avg_weight_by_units_in_price_in -
              row.avg_weight_by_units_in_price_in) *
            row.sum_units_in
          : 0),
      baseUnits: acc.baseUnits + (priceDropped ? baseRow?.sum_units : 0),
      compareUnits: acc.compareUnits + (priceDropped ? row.sum_units : 0),
    };
  }, valuesForROI);
  // ROI numerator (incremental margin) depends on the tenant's strategy.
  const numerator = roiNumerator(roiStrategy, {
    marginEsc: compareMargin,
    marginRef: baseMargin,
    unitsEsc: compareUnits,
    unitsRef: baseUnits,
  });
  const calculatedROI = (numerator / investmentIn) * 100;
  return Number.isNaN(calculatedROI) ? 0 : calculatedROI;
};

const calculateLiftedKpis = (
  baseData,
  compareData,
  referenceData,
  roiStrategy = "standard"
) => {
  const price = roundToTwoDecimals(calculateWeightedPrice(compareData));
  const liftPrice =
    ((roundToTwoDecimals(calculateWeightedPrice(compareData)) -
      roundToTwoDecimals(calculateWeightedPrice(baseData))) /
      roundToTwoDecimals(calculateWeightedPrice(baseData))) *
    100;
  const liftUnits =
    ((calculateAggregates(compareData, "sum_units") -
      calculateAggregates(baseData, "sum_units")) /
      calculateAggregates(baseData, "sum_units")) *
    100;
  const liftSales =
    ((calculateAggregates(compareData, "sum_sales") -
      calculateAggregates(baseData, "sum_sales")) /
      calculateAggregates(baseData, "sum_sales")) *
    100;
  const liftMargin =
    ((calculateAggregates(compareData, "sum_margin") -
      calculateAggregates(baseData, "sum_margin")) /
      calculateAggregates(baseData, "sum_margin")) *
    100;
  const liftRetailerMargin =
    ((calculateAggregates(compareData, "sum_retailer_margin") -
      calculateAggregates(baseData, "sum_retailer_margin")) /
      calculateAggregates(baseData, "sum_retailer_margin")) *
    100;

  // Calcular ROI de cada escenario vs referencia, luego restar para obtener diferencia en pp
  const baseROI = calculateROI(referenceData, baseData, roiStrategy);
  const compareROI = calculateROI(referenceData, compareData, roiStrategy);
  const roi = compareROI - baseROI;

  return {
    price,
    liftPrice,
    liftUnits,
    liftSales,
    liftMargin,
    liftRetailerMargin,
    roi,
  };
};

const ScenarioMatrixCell = ({
  cellId,
  cellData,
  selectedScenariosData,
  referenceScenarioId,
  scenarioBaseDirection,
  scenarioBaseName,
  scenarioToCompareName,
}) => {
  const classes = useStyles();
  const roiStrategy = useSelector((state) => state.auth.configCatUser)?.features
    ?.roiStrategy;
  const { scenarioBaseId, scenarioToCompareId, isDiagonal } = cellData;
  const scenarioBaseData = selectedScenariosData[scenarioBaseId];
  const scenarioToCompareData = selectedScenariosData[scenarioToCompareId];
  const referenceData = selectedScenariosData[referenceScenarioId];

  const getKpiColor = (value, isPrice) => {
    if (isPrice) return "";
    if (value > 0) return "#37a57b";
    if (value < 0) return "#e03a3a";
    return "#000000";
  };

  const liftedKpis = calculateLiftedKpis(
    scenarioBaseDirection === "row" ? scenarioToCompareData : scenarioBaseData,
    scenarioBaseDirection === "row" ? scenarioBaseData : scenarioToCompareData,
    referenceData,
    roiStrategy
  );

  if (isDiagonal) {
    return (
      <Box className={`${classes.matrixCell} ${classes.noDataCell}`}>
        <div style={{ textAlign: "center" }}>
          <HiOutlineLockClosed
            size={25}
            style={{ color: "#E0E0E0", fontWeight: "lighter" }}
          />
        </div>
      </Box>
    );
  }

  if (!(scenarioBaseId && scenarioToCompareId)) {
    return (
      <Box className={`${classes.matrixCell} ${classes.noDataCell}`}>
        <div style={{ textAlign: "center" }}>
          <VscNewFolder
            size={25}
            style={{ color: "#E0E0E0", fontWeight: "lighter" }}
          />
        </div>
      </Box>
    );
  }

  return (
    <Tooltip
      placement="top"
      title={
        <>
          <div className={classes.tooltipCellInfo}>
            <div>
              {scenarioBaseDirection === "row"
                ? scenarioBaseName
                : scenarioToCompareName}
            </div>
            <div>
              <strong>VS</strong>
            </div>
            <div>
              {scenarioBaseDirection === "row"
                ? scenarioToCompareName
                : scenarioBaseName}
            </div>
          </div>
        </>
      }
      style={{ cursor: "default" }}
    >
      <Box className={classes.matrixCell}>
        {KPI_DEFINITIONS.map(({ name, key, isCurrency }) => (
          <div key={key} className={classes.cellKpiContainer}>
            <div>{name}</div>
            <div
              style={{
                color: getKpiColor(liftedKpis[key], isCurrency),
              }}
            >
              {isCurrency ? "$" : ""}
              {key !== "price" && liftedKpis[key] > 0 ? "+" : ""}
              {liftedKpis[key]?.toFixed(2) || 0}
              {key === "roi" ? " pp" : key !== "price" ? "%" : ""}
            </div>
          </div>
        ))}
      </Box>
    </Tooltip>
  );
};

export default ScenarioMatrixCell;
